import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import BlurredCard from "../BlurredCard";
import { Procedures, ServiceRequest, Encounter } from "src/shared/types";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { apiGet, apiPost } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableBadge from "../table/TableBadge";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";
import PractitionerLink from "../table/PractitionerLink";
import ActionsMenu from "../ActionsMenu";
import { useToast } from "../Toast";
import { ActiveTableFilter } from "../ActiveTableFilter";

type AddBillingModalProps = {
  procedure: Procedures;
  onClose: () => void;
  onSubmit: (procedure: Procedures, quantity: number, amount: string) => void;
  submitting: boolean;
};

const AddBillingModal = ({ procedure, onClose, onSubmit, submitting }: AddBillingModalProps) => {
  const [ quantity, setQuantity ] = useState(1);
  const [ amount, setAmount ] = useState("0.00");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed) || parsed < 0) return;
    onSubmit(procedure, quantity, amount);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" aria-hidden onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-medium text-gray-900">Add Test Charge</h3>
        <p className="mt-1 text-sm text-gray-500">{procedure.name}</p>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label htmlFor="add-billing-quantity" className="block text-sm font-medium text-gray-700">
              Quantity
            </label>
            <input
              id="add-billing-quantity"
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label htmlFor="add-billing-amount" className="block text-sm font-medium text-gray-700">
              Amount
            </label>
            <input
              id="add-billing-amount"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !amount.trim()}
              className="rounded-md border border-transparent bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

const procedureStatusColorMap: Record<string, string> = {
  "completed": "bg-emerald-500",
  "in-progress": "bg-amber-500",
  "preparation": "bg-sky-500",
  "not-done": "bg-rose-500",
};

const serviceRequestStatusColorMap: Record<string, string> = {
  active: "bg-emerald-500",
  completed: "bg-sky-500",
  revoked: "bg-rose-500",
  draft: "bg-gray-500",
};

const columns: StaticDataColumnProps[] = [
  { title: "Name", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Provider", sortable: true },
  { title: "Type", sortable: true },
  { title: "Status", sortable: true },
  { title: "Location", sortable: true },
  { title: "Reason", sortable: false },
];

type CombinedItem = (Procedures | ServiceRequest) & { itemType: 'procedure' | 'service-request' };

const convertToRows = (
  items: CombinedItem[],
  onAddBilling: ((procedure: Procedures) => void) | null,
  addBillingSubmitting: boolean,
  encounterFilter: Encounter | null | undefined
): StaticDataRow[][] => {
  const showActions = onAddBilling != null;

  return items.map((item) => {
    const isProcedure = item.itemType === 'procedure';
    const procedure = item as Procedures;
    const serviceRequest = item as ServiceRequest;

    const baseCells: StaticDataRow[] = [
      {
        name: "Name",
        value: item.name,
        element: <span className="text-gray-900">{item.name}</span>
      },
      {
        name: "Date & Time",
        value: getDateTimeSortValue(item.date, isProcedure ? procedure.startTime : serviceRequest.time),
        element: (
          isProcedure ? (
            <TableDateTime
              date={item.date}
              startTime={procedure.startTime}
              endTime={procedure.endTime}
            />
          ) : (
            <TableDateTime
              date={item.date}
              startTime={serviceRequest.time}
            />
          )
        )
      },
      {
        name: "Type",
        value: item.itemType,
        element: (
          <TableBadge
            text={item.itemType === 'procedure' ? 'Procedure' : 'Service Request'}
            bgColorClass={item.itemType === 'procedure' ? 'bg-orange-500' : 'bg-indigo-500'}
          />
        )
      },
      {
        name: "Status",
        value: item.status,
        element: isProcedure
          ? (
            <TableBadge
              text={capitalizeStatus(item.status)}
              bgColorClass={procedureStatusColorMap[item.status] ?? "bg-gray-500"}
            />
          )
          : (
            <TableBadge
              text={capitalizeStatus(item.status)}
              bgColorClass={serviceRequestStatusColorMap[item.status] ?? "bg-gray-500"}
            />
          )
      },
      {
        name: "Provider",
        value: isProcedure ? (procedure.recorder?.name || "—") : (serviceRequest.requestor?.name || "—"),
        element: (
          <PractitionerLink
            name={isProcedure ? (procedure.recorder?.name || "—") : (serviceRequest.requestor?.name   || "—")}
            practitionerId={isProcedure ? procedure.recorder?.practitioner_id : serviceRequest.requestor?.practitioner_id}
          />
        )
      },
      {
        name: "Location",
        value: isProcedure ? (procedure.location || "—") : (serviceRequest.category || "—"),
        element: (
          <span className="text-gray-600 text-sm">
            {isProcedure ? (procedure.location || "—") : (serviceRequest.category || "—")}
          </span>
        )
      },
      {
        name: "Reason",
        value: item.reasonCode || "—",
        element: <span className="text-gray-600 text-sm">{item.reasonCode || "—"}</span>
      },
    ];

    if (showActions && onAddBilling) {
      const effectiveEncounter = encounterFilter || procedure.encounter;
      const hasPractitioner = procedure.recorder || (effectiveEncounter as Encounter | null)?.practitioner;
      const canShowActionMenu = isProcedure && effectiveEncounter && !!hasPractitioner;
      const procedureActions = canShowActionMenu
        ? [
            {
              id: "add-billing",
              label: "Add Test Charge",
              onClick: () => onAddBilling(procedure),
              disabled: addBillingSubmitting,
            },
          ]
        : [];
      const actions = isProcedure ? procedureActions : [];
      baseCells.push({
        name: "Actions",
        value: "",
        element: <ActionsMenu actions={actions} />
      });
    }

    return baseCells;
  });
};

const dummyProcedures: Procedures[] = [
  {
    name: "Procedure 1",
    id: "1",
    identifiers: [
      {
        name: "CPT",
        objectIdentifier: "12345",
        value: "12345",
      },
    ],
    date: new Date().toISOString().split('T')[0],
    startTime: "10:00",
    endTime: "11:00",
    status: "completed",
    location: "Location 1",
    recorder: {
      practitioner_id: "123",
      name: "Recorder 1",
    },
    reasonCode: "Reason 1",
    encounter: null
  },
  {
    name: "Procedure 2",
    id: "2",
    identifiers: [
      {
        name: "CPT",
        objectIdentifier: "12345",
        value: "12345",
      },
    ],
    date: new Date().toISOString().split('T')[0],
    startTime: "12:00",
    endTime: "13:00",
    status: "completed",
    location: "Location 2",
    recorder: {
      practitioner_id: "456",
      name: "Performer 2",
    },
    reasonCode: "Reason 2",
    encounter: null
  },
];

const dummyServiceRequests: ServiceRequest[] = [
  {
    id: "1",
    name: "Service Request 1",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "active",
    requestor: {
      practitioner_id: "123",
      name: "Requestor 1",
    },
    category: "Category 1",
    reasonCode: "Reason 1",
  },
];

type ProceduresCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
  encounterFilter?: Encounter | null;
  onClearEncounterFilter?: () => void;
};

const ProceduresCard = ({ yearFrom, yearTo, refreshKey, encounterFilter = null, onClearEncounterFilter }: ProceduresCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId, patientData } = useOrganizationContext();
  const { Toast, showToast } = useToast();

  const [ procedures, setProcedures ] = useState<Procedures[]>([]);
  const [ proceduresLoading, setProceduresLoading ] = useState(false);
  const [ serviceRequests, setServiceRequests ] = useState<ServiceRequest[]>([]);
  const [ serviceRequestsLoading, setServiceRequestsLoading ] = useState(false);
  const [ addBillingSubmitting, setAddBillingSubmitting ] = useState(false);
  const [ billingModalProcedure, setBillingModalProcedure ] = useState<Procedures | null>(null);

  const { isVerified } = useVerification();

  const handleOpenBillingModal = useCallback((procedure: Procedures) => {
    setBillingModalProcedure(procedure);
  }, []);

  const handleSubmitBilling = useCallback(async (procedure: Procedures, quantity: number, amount: string) => {
    const encounter = encounterFilter ?? procedure.encounter;
    if (!encounter || !patientData || !organization || !patientId) return;
    setAddBillingSubmitting(true);
    try {
      const response = await apiPost(`/api/${organization.id}/upload-billing/${patientId}`, {
        csn: encounter.csn || null,
        patientIdentifier: patientData.primaryIdentifier,
        lastName: patientData.lastName,
        givenNames: patientData.givenNames || [],
        dob: patientData.dateOfBirth,
        practitioner: encounter.practitioner || procedure.recorder,
        procedureIdentifiers: procedure.identifiers,
        quantity,
        amount,
      });
      if (response.ok) {
        showToast('Billing uploaded successfully', 'success');
        setBillingModalProcedure(null);
      } else {
        showToast('Failed to upload billing', 'error');
      }
    } catch {
      showToast('Failed to upload billing', 'error');
    } finally {
      setAddBillingSubmitting(false);
    }
  }, [ encounterFilter, patientData, organization, patientId, showToast ]);

  const fetchProcedures = useCallback(async () => {
    if (!organization || !patientId) {
      setProcedures([]);
      setProceduresLoading(false);
      return;
    }

    setProceduresLoading(true);


    try {
      const params: Record<string, string> = { yearFrom, yearTo };
      if (encounterFilter?.id) params.encounterId = encounterFilter.id;
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/procedures`, params);
      const data = await response.json();

      if (!data.error) {
        setProcedures(data.procedures);
      } else {
        setProcedures([]);
        window.location.href = "/error";
      }
    } catch (_) {
      setProcedures([]);
    } finally {
      setProceduresLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo, encounterFilter ]);

  const fetchServiceRequests = useCallback(async () => {
    if (!organization || !patientId) {
      setServiceRequests([]);
      setServiceRequestsLoading(false);
      return;
    }

    setServiceRequestsLoading(true);

    try {
      const params: Record<string, string> = { yearFrom, yearTo };
      if (encounterFilter?.id) params.encounterId = encounterFilter.id;
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/service-requests`, params);
      const data = await response.json();

      if (data.error) {
        setServiceRequests([]);
        window.location.href = "/error";
      }

      setServiceRequests(data.serviceRequests);
    } catch (_) {
      setServiceRequests([]);
    } finally {
      setServiceRequestsLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo, encounterFilter ]);

  useEffect(() => {
    if (isVerified) {
      fetchProcedures();
      fetchServiceRequests();
    } else {
      setProcedures(dummyProcedures);
      setServiceRequests(dummyServiceRequests);
    }
  }, [ isVerified, fetchProcedures, fetchServiceRequests, yearFrom, yearTo, refreshKey, encounterFilter ]);

  const combinedList: CombinedItem[] = useMemo(() => {
    return [
      ...procedures.map(p => ({ ...p, itemType: 'procedure' as const })),
      ...serviceRequests.map(r => ({ ...r, itemType: 'service-request' as const })),
    ];
  }, [ procedures, serviceRequests ]);

  const showLoading = isVerified && (proceduresLoading || serviceRequestsLoading);

  const tableRows = useMemo(
    () => convertToRows(
      combinedList,
      patientData && organization ? handleOpenBillingModal : null,
      addBillingSubmitting,
      encounterFilter
    ),
    [ combinedList, patientData, organization, handleOpenBillingModal, addBillingSubmitting, encounterFilter ]
  );

  return (
    <BlurredCard>
      {encounterFilter && (
        <ActiveTableFilter
          clearLabel="Show all procedures"
          onClear={onClearEncounterFilter}
        >
          Showing procedures for: <span className="font-medium text-gray-900">{encounterFilter.type}</span>
          {encounterFilter.date && encounterFilter.date !== 'Unknown date' && (
            <> — {new Date(encounterFilter.date).toLocaleDateString()}</>
          )}
          {encounterFilter.practitioner && <> — <PractitionerLink name={encounterFilter.practitioner?.name || "—"} practitionerId={encounterFilter.practitioner?.practitioner_id} /></>}
        </ActiveTableFilter>
      )}
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Procedures..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {combinedList.length > 0 ? (
            <StaticDataTable
              tableId="procedures-table"
              columns={columns}
              rows={tableRows}
              updateUrlParams={false}
              trailingColumnKey={patientData && organization ? "Actions" : undefined}
              rowId={(item) => `${item.Type}-${item.Name}-${item.Date}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <ClipboardDocumentListIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No procedures or service requests found</p>
            </div>
          )}
        </div>
      )}
      <Toast />
      {billingModalProcedure && (
        <AddBillingModal
          procedure={billingModalProcedure}
          onClose={() => setBillingModalProcedure(null)}
          onSubmit={handleSubmitBilling}
          submitting={addBillingSubmitting}
        />
      )}
    </BlurredCard>
  );
};

export default ProceduresCard;
